import { Avatar, Button, keyframes, Paper, Typography } from '@mui/material';
import { IRecommendations } from 'API/Home/recommendations';
import {
  ArrowBackBlue,
  BlackX,
  Cart,
  Collapse,
  equipmentIcon1,
  Expand,
  OrderTreatments,
  SquareArrowCorner,
  UniversalRequestSubject7,
} from 'Images';
import { RecommendationInformation } from './RecoomendationInformation';
import { useState } from 'react';
import { DeleteRecommendation } from './DeleteRecommendation';
import { Buttons, IButtonSubject } from './Buttons';
import { useDictionary, useIsDesktop } from 'Hooks';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { RECOMMENDATIONS_TYPES } from 'Enum/recommendation';
import { useNavigate } from 'react-router-dom';
import { IMedication } from 'API/medications';

interface IRecommendationCard {
  recommendationItem: IRecommendations;
  removeRecommendation: Function;
  setShowDialog?: () => void;
  medicationList?: IMedication[];
}

interface ISubjectTypeObject {
  icon: string;
  deleteReqPath: string;
  btnLeft: IButtonSubject;
  btnRight: IButtonSubject;
}

export const RecommendationCard = ({
  recommendationItem,
  removeRecommendation,
  setShowDialog,
  medicationList,
}: IRecommendationCard) => {
  const [isFront, setIsFront] = useState<boolean>(true);
  const [animation, setAnimation] = useState<any>(null);
  const [showMedicationList, setShowMedicationList] = useState(false);

  const navigate = useNavigate();

  const isDesktop = useIsDesktop();
  const dictionary = useDictionary(
    Dictionary,
    DICTIONARY.Home
  ).userRecommendations;

  const rotateCardStepOne = keyframes`from{ transform: rotateY(0)} to{transform: rotateY(90deg)}`;
  const rotateCardStepTwo = keyframes`from{transform: rotateY(90deg)} to{transform: rotateY(0)}`;

  const animationHandler = (showFront: boolean) => {
    setAnimation(rotateCardStepOne);
    setTimeout(() => {
      setIsFront(showFront);
      setAnimation(rotateCardStepTwo);
    }, 450);
    setTimeout(() => {
      setAnimation(null);
    }, 1000);
  };

  const subjectTypeObjectCreatorHandler = (
    subjectId: number
  ): ISubjectTypeObject | null => {
    if (!recommendationItem || !recommendationItem.entityJSON) {
      return null;
    }

    switch (subjectId) {
      case RECOMMENDATIONS_TYPES.LONG_TERM_EQUIPMENT: {
        return {
          icon: Cart,
          deleteReqPath: 'long-term-equip/close',
          btnLeft: {
            icon: ArrowBackBlue,
            text: dictionary.claimedLongTermEquipment,
            onClick: () => navigate('/longterm-equipment'),
          },
          btnRight: {
            icon: SquareArrowCorner,
            text: dictionary.requestReferral,
            onClick: () => navigate('universalRequest/1/2'),
          },
        };
      }
      case RECOMMENDATIONS_TYPES.MEDICATIONS_EXPIRE_MSG_SINGLE: {
        return {
          icon: UniversalRequestSubject7,
          deleteReqPath: 'expired-meds/close',
          btnLeft: {
            icon: ArrowBackBlue,
            text: dictionary.uploadNewPrescription,
            onClick: () => navigate('/universalRequest/1/6/94'),
          },
          btnRight: {
            icon: SquareArrowCorner,
            text: dictionary.requestPrescription,
            onClick: () => navigate('universalRequest/1/1/77'),
          },
        };
      }
      case RECOMMENDATIONS_TYPES.MEDICATIONS_EXPIRE_MSG_GROUP: {
        return {
          icon: UniversalRequestSubject7,
          deleteReqPath: 'expired-meds/close',
          btnLeft: {
            icon: ArrowBackBlue,
            text: dictionary.uploadNewPrescription,
            onClick: () => navigate('/universalRequest/1/6/94'),
          },
          btnRight: {
            icon: SquareArrowCorner,
            text: dictionary.requestPrescription,
            onClick: () => navigate('universalRequest/1/1/77'),
          },
        };
      }
      case RECOMMENDATIONS_TYPES.CONSUMEQUIP_ORDER_REMINDER_SINGLE: {
        const reqNumber = recommendationItem.entityJSON.items?.[0]?.reqNumber;
        return {
          icon: equipmentIcon1,
          deleteReqPath: reqNumber
            ? `/close/${reqNumber.toString()}`
            : '/close',
          btnLeft: {
            icon: ArrowBackBlue,
            text: dictionary.yourEquipment,
            onClick: () => navigate('/equipment'),
          },
          btnRight: {
            icon: SquareArrowCorner,
            text: dictionary.requestReferral,
            onClick: () => navigate('universalRequest/1/1/77'),
          },
        };
      }
      case RECOMMENDATIONS_TYPES.TREATMENTS_ORDER_REMINDER_SINGLE: {
        const reqNumber = recommendationItem.entityJSON.items?.[0]?.reqNumber;
        return {
          icon: UniversalRequestSubject7,
          deleteReqPath: reqNumber
            ? `/close/${reqNumber.toString()}`
            : '/close',
          btnLeft: {
            icon: OrderTreatments,
            text: dictionary.yourTreatments,
            onClick: () => navigate('/treatments'),
          },
          btnRight: {
            icon: SquareArrowCorner,
            text: dictionary.treatmentsInfo,
            onClick: () =>
              navigate(
                'https://shikum.mod.gov.il/medical/relaxation/hotel-accesability'
              ),
          },
        };
      }
      default:
        return null;
    }
  };

  const subjectTypeObject: ISubjectTypeObject | null =
    subjectTypeObjectCreatorHandler(recommendationItem.subjectId);

  if (!subjectTypeObject) return null;

  return (
    <Paper
      sx={{
        position: 'relative',
        p: '20px 0 0 0',
        minWidth: isDesktop ? '388px' : '100%',
        minHeight: '200px',
        display: 'flex',
        flexFlow: 'column',
        borderRadius: '16px',
        border: '1px solid',
        borderColor: 'primary.shade_200',
        bgcolor: isFront ? '#FFF' : '#E7EDFF',
        boxShadow: 'none',
        animation: animation ? `${animation} .5s linear` : undefined,
      }}
    >
      {isFront && (
        <Avatar
          alt={dictionary.deleteBtnAlt}
          src={BlackX}
          sx={{
            position: 'absolute',
            top: 10,
            right: 10,
            width: '22px',
            height: '22px',
            p: '3px',
            ':hover': {
              cursor: 'pointer',
              outline: '1px solid black',
              borderRadius: '50%',
            },
          }}
          onClick={() => animationHandler(false)}
          tabIndex={0}
        />
      )}
      {isFront ? (
        <>
          <RecommendationInformation
            title={recommendationItem.title}
            description={recommendationItem.description}
            icon={subjectTypeObject.icon}
          />

          {showMedicationList && (
            <Button
              sx={{
                p: 0,
                alignItems: 'center',
                justifyContent: 'flex-start',
                gap: '6px',
                mt: showMedicationList ? '4px' : undefined,
              }}
              onClick={
                isDesktop
                  ? () => setShowDialog && setShowDialog()
                  : () => setShowMedicationList(!showMedicationList)
              }
            >
              <Typography
                variant='body5'
                sx={{
                  textDecorationLine: isDesktop ? 'underline' : undefined,
                }}
              >
                {showMedicationList
                  ? dictionary.closeList
                  : dictionary.openList}
              </Typography>
              {!isDesktop && (
                <Avatar
                  src={showMedicationList ? Collapse : Expand}
                  alt=''
                  sx={{ width: '20px', height: '20px' }}
                />
              )}
            </Button>
          )}
        </>
      ) : (
        <DeleteRecommendation
          animationHandler={animationHandler}
          removeRecommendation={removeRecommendation}
          deleteRequest={{
            path: subjectTypeObject.deleteReqPath,
            body: {
              reqNumber:
                recommendationItem.entityJSON.items?.[0]?.reqNumber?.toString() ??
                '',
              nextEntitleDate:
                recommendationItem.entityJSON.items?.[0]?.nextEntitleDate ?? '',
            },
          }}
        />
      )}
      {isFront && (
        <Buttons
          leftBtn={subjectTypeObject.btnLeft}
          rightBtn={subjectTypeObject.btnRight}
        />
      )}
    </Paper>
  );
};
